/**
 * Formats a timestamp into a human-readable date and time string.
 *
 * @param timestamp - The timestamp to format. Can be a Date object, ISO string, Unix timestamp, or null/undefined.
 * @returns A formatted string in the format "MM/DD/YYYY HH:MM am/pm" (e.g., "01/24/2026 11:24 AM").
 *          Returns "-" if the timestamp is invalid, null, or undefined.
 *
 * @example
 * formatTimestamp(new Date("2026-01-24T11:24:00")) // "01/24/2026 11:24 AM"
 * formatTimestamp("2026-01-24T11:24:00Z") // "01/24/2026 11:24 AM"
 * formatTimestamp(1737718440000) // "01/24/2026 11:24 AM"
 * formatTimestamp(null) // "-"
 * formatTimestamp(undefined) // "-"
 */
export function formatTimestamp(
  timestamp: string | number | Date | null | undefined,
): string {
  if (!timestamp) return "-";

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (isNaN(date.getTime())) return "-";

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();

  const time = date.toLocaleString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${month}/${day}/${year} ${time}`;
}

/**
 * Formats a Unix timestamp into a short date string.
 *
 * @param timestamp - Unix timestamp in milliseconds.
 * @returns A formatted string in the format "DD Mon YYYY" (e.g., "24 Jan 2026").
 *
 * @example
 * formatDateShort(1737718440000) // "24 Jan 2026"
 */
export const formatDateShort = (timestamp: number): string => {
  const date = new Date(timestamp);

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
